import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, HelpCircle, Link2, ArrowRight, Check, Zap, Flame } from "lucide-react";

/**
 * Play With Friends — Group Mode entry screen.
 * Mimics the cinematic orange/red/pink party-energy reference.
 */

const VIBE_TILES = [
  { id: "most-likely-to", emoji: "😂", title: "Most Likely To", accent: "#FF8A00", bg: "rgba(255, 138, 0, 0.12)" },
  { id: "red-flag-radar", emoji: "🚩", title: "Red Flag\nRadar",   accent: "#FF8A00", bg: "rgba(255, 184, 0, 0.10)" },
  { id: "truth-or-drink", emoji: "🍷", title: "Truth or\nDrink",   accent: "#C966FF", bg: "rgba(201, 102, 255, 0.10)" },
  { id: "exposed",        emoji: "💀", title: "Exposed",           accent: "#FF8A00", bg: "rgba(255, 184, 0, 0.10)" },
  { id: "hot-takes",      emoji: "🔥", title: "Hot Takes",         accent: "#FF8A00", bg: "rgba(255, 138, 0, 0.10)" },
  { id: "friendship-test",emoji: "👥", title: "Friendship\nTest",  accent: "#9B6BFF", bg: "rgba(155, 107, 255, 0.12)" },
];

const FEATURES = [
  { emoji: "😂", title: "Vote chaos",      sub: "Everyone picks." },
  { emoji: "👀", title: "Reveal secrets",  sub: "Anonymous\nanswers." },
  { emoji: "🎉", title: "Party reactions", sub: "Live group\nreveals." },
];

const BOTTOM_INFO = [
  { icon: Check, color: "#3DD68C", label: "No signup" },
  { icon: Zap,   color: "#FFE082", label: "Instant laughs" },
  { icon: Flame, color: "#FF8A00", label: "Best with friends" },
];

function SpeechBubble({ text, accent, style }) {
  return (
    <div
      className="absolute z-[6] px-3 py-1.5 rounded-2xl"
      style={{
        background: "rgba(20, 10, 14, 0.85)",
        border: `1.5px solid ${accent}`,
        boxShadow: `0 0 14px ${accent}aa, inset 0 0 8px ${accent}33`,
        ...style,
      }}
    >
      <span className="text-white text-[11px] font-extrabold leading-tight whitespace-pre-line" style={{ textShadow: `0 0 6px ${accent}` }}>
        {text}
      </span>
    </div>
  );
}

function SparkBurst({ color, size = 28, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" {...rest}>
      <g style={{ filter: `drop-shadow(0 0 4px ${color})` }} stroke={color} strokeWidth="1.6" strokeLinecap="round" fill="none">
        <line x1="30" y1="8" x2="30" y2="22" /><line x1="30" y1="38" x2="30" y2="52" />
        <line x1="8" y1="30" x2="22" y2="30" /><line x1="38" y1="30" x2="52" y2="30" />
        <line x1="14" y1="14" x2="22" y2="22" /><line x1="38" y1="38" x2="46" y2="46" />
        <line x1="46" y1="14" x2="38" y2="22" /><line x1="22" y1="38" x2="14" y2="46" />
      </g>
    </svg>
  );
}

function NeonHeartOutline({ size = 36, color = "#FF8A00", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
      <path
        d="M12 21s-7-4.5-7-10a4.5 4.5 0 0 1 8-2.85A4.5 4.5 0 0 1 19 11c0 5.5-7 10-7 10z"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        style={{ filter: `drop-shadow(0 0 4px ${color}) drop-shadow(0 0 9px ${color})` }}
      />
    </svg>
  );
}

export default function PlayGroupEntry({ onStart, onJoin, onPickVibe, onBack }) {
  const [selected, setSelected] = useState("most-likely-to");

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="group-entry-screen">
      {/* Warm orange bloom backdrop */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          top: 80, left: "50%", transform: "translateX(-50%)",
          width: 540, height: 540,
          background:
            "radial-gradient(circle, rgba(255, 138, 0, 0.40) 0%, rgba(255, 184, 0, 0.18) 35%, transparent 70%)",
          filter: "blur(48px)",
          zIndex: 0,
        }}
        aria-hidden
      />

      {/* Top bar: back · GROUP MODE pill · help */}
      <div className="relative z-[10] flex items-center justify-between px-5 pt-5">
        <button
          onClick={onBack}
          aria-label="Back"
          data-testid="group-entry-back"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)" }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div
          className="px-4 py-1.5 rounded-full flex items-center gap-1.5"
          style={{
            background: "rgba(255, 138, 0, 0.10)",
            border: "1.5px solid #FF8A00",
            boxShadow: "0 0 14px rgba(255, 138, 0, 0.55), inset 0 0 8px rgba(255, 138, 0, 0.15)",
          }}
        >
          <span className="text-[14px]">🎉</span>
          <span
            className="font-black tracking-[0.22em] text-[11px]"
            style={{
              background: "linear-gradient(90deg, #FFE082 0%, #FF8A00 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
            data-testid="group-entry-pill"
          >
            GROUP MODE
          </span>
        </div>

        <button
          aria-label="Help"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/55 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)" }}
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-3 pb-16">
        {/* Floating decoration emojis around title */}
        <div className="absolute z-[3] text-[32px]" style={{ top: 20, left: 8, filter: "drop-shadow(0 0 8px rgba(255,107,31,0.7))" }}>😂</div>
        <div className="absolute z-[3] text-[26px]" style={{ top: 12, right: 10, filter: "drop-shadow(0 0 8px rgba(255,107,31,0.8))" }}>🔥</div>
        <SparkBurst color="#FF8A00" size={24} className="absolute z-[3]" style={{ top: 70, right: 56 }} />
        <SparkBurst color="#FF8A00" size={22} className="absolute z-[3]" style={{ top: 110, left: 28 }} />
        <NeonHeartOutline size={38} color="#FF8A00" className="absolute z-[3]" style={{ top: 156, left: 4 }} />

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-[4] text-center mt-3 mb-3"
        >
          <h1
            className="text-white font-extrabold leading-[0.92] tracking-tight"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.6rem, 12.5vw, 3.4rem)",
              letterSpacing: "-0.035em",
            }}
            data-testid="group-entry-title"
          >
            Play
            <br />
            <span className="vc-title vc-title-gold">
              With Friends
            </span>
          </h1>
          {/* underline accent */}
          <div className="flex justify-center mt-1">
            <div
              style={{
                width: 180,
                height: 3,
                background: "linear-gradient(90deg, transparent, #FF8A00, #FF8A00, transparent)",
                borderRadius: 999,
                filter: "drop-shadow(0 0 6px rgba(255, 184, 0, 0.7))",
              }}
            />
          </div>
          <p className="text-white mt-3 font-semibold" style={{ fontSize: "14px" }}>
            More people = more drama 👀
          </p>
        </motion.div>

        {/* Hero image (cinematic 5-friends-around-glowing-phone) — mobile-fit */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative -mx-3 mb-3"
          style={{ height: "32vh", overflow: "hidden" }}
        >
          <img
            src="/assets/home/hero-group-cinematic.png"
            alt=""
            className="w-full h-full object-cover"
            style={{
              filter:
                "drop-shadow(0 0 32px rgba(255, 138, 0, 0.55)) drop-shadow(0 0 70px rgba(255, 138, 0, 0.3))",
            }}
          />
          {/* Speech bubbles overlaying the photo */}
          <SpeechBubble
            text={"Expose\nthem 😂"}
            accent="#FF8A00"
            style={{ left: 4, top: "48%", transform: "rotate(-3deg)" }}
          />
          <SpeechBubble
            text={"This is\nNOT safe 😭"}
            accent="#9B6BFF"
            style={{ right: 4, top: "12%", transform: "rotate(3deg)" }}
          />
          <SpeechBubble
            text={"No way\nbro 😳"}
            accent="#FF8A00"
            style={{ right: 4, top: "58%", transform: "rotate(3deg)" }}
          />
          {/* corner micro-accents */}
          <div className="absolute text-[22px]" style={{ left: 8, bottom: 18, filter: "drop-shadow(0 0 8px rgba(255,180,71,0.85))" }}>🤩</div>
          <div className="absolute text-[22px]" style={{ right: 14, bottom: 30, filter: "drop-shadow(0 0 8px rgba(155,107,255,0.85))" }}>😈</div>
        </motion.div>

        {/* 3 feature cards */}
        <section className="grid grid-cols-3 gap-2.5 mb-5" data-testid="group-entry-features">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className="rounded-2xl px-2.5 py-3 flex flex-col items-center text-center"
              style={{
                background: "rgba(20, 10, 14, 0.55)",
                border: "1.2px solid rgba(255, 138, 0, 0.45)",
                boxShadow: "0 0 14px rgba(255, 138, 0, 0.20), inset 0 0 10px rgba(255, 138, 0, 0.05)",
              }}
              data-testid={`group-entry-feature-${i}`}
            >
              <span className="text-[26px] mb-1.5" style={{ filter: "drop-shadow(0 0 6px rgba(255, 138, 0, 0.7))" }}>
                {f.emoji}
              </span>
              <div className="text-white text-[12.5px] font-extrabold leading-tight">{f.title}</div>
              <div className="text-[#B8B8CC] text-[10.5px] mt-0.5 leading-tight whitespace-pre-line" style={{ fontWeight: 500 }}>
                {f.sub}
              </div>
            </div>
          ))}
        </section>

        {/* CTA — Start the chaos */}
        <button
          onClick={() => onStart?.(selected)}
          data-testid="group-entry-start"
          className="vc-cta vc-cta-primary w-full rounded-full py-4 font-extrabold flex items-center justify-center gap-2 mb-3"
          style={{ fontSize: "17px", letterSpacing: "0.01em" }}
        >
          Start the chaos
          <ArrowRight className="w-5 h-5" strokeWidth={3} />
        </button>

        {/* Outlined Join with code — secondary */}
        <button
          onClick={onJoin}
          data-testid="group-entry-join"
          className="vc-cta vc-cta-secondary w-full rounded-full py-3.5 font-extrabold flex items-center justify-center gap-2 mb-5"
          style={{ fontSize: "15px" }}
        >
          <Link2 className="w-4 h-4" />
          Join with code
        </button>

        {/* Bottom info row */}
        <div className="flex items-center justify-between px-2" data-testid="group-entry-bottom-info">
          {BOTTOM_INFO.map((b) => (
            <div key={b.label} className="flex items-center gap-1.5">
              <b.icon
                className="w-3.5 h-3.5"
                style={{ color: b.color, filter: `drop-shadow(0 0 4px ${b.color})` }}
                strokeWidth={3}
                fill={b.icon === Flame ? b.color : "none"}
              />
              <span className="text-white/80 text-[11.5px] font-semibold">{b.label}</span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
